import { Billboard } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import * as THREE from "three";
import type { AvatarState } from "../../shared/types";
import type { RoomMotionFeed } from "../hooks/useRoomConnection";
import { DESIRED_HEIGHT } from "../lib/drobbit-model";
import { createBubbleTexture, type BubbleResult } from "./DrobbitAvatar";

const CHAT_BUBBLE_FADE_MS = 1500;
const CHAT_BUBBLE_MIN_MS = 5500;
const CHAT_BUBBLE_MAX_MS = 12000;
const CHAT_BUBBLE_BASE_MS = 3800;
const CHAT_BUBBLE_PER_CHAR_MS = 60;
const CHAT_BUBBLE_SCREEN_GAP_PX = 2;
const CHAT_BUBBLE_WORLD_BASE_Y = DESIRED_HEIGHT + 0.15;
const CHAT_BUBBLE_STALE_GRACE_MS = 3000;
const MAX_ACTIVE_BUBBLES = 40;
const CHAT_BUBBLE_BASE_SCALE = 1.9;
const CHAT_BUBBLE_REFERENCE_ZOOM = 46;
const CHAT_BUBBLE_MIN_ZOOM_SCALE = 1.0;
const CHAT_BUBBLE_MAX_ZOOM_SCALE = 1.72;

interface AnchoredChatBubble {
  id: string;
  avatarId: string;
  at: number;
  lifetimeMs: number;
  anchorX: number;
  anchorY: number;
  anchorZ: number;
  bubble: BubbleResult;
}

interface ScreenRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface Props {
  roomId: string | null;
  avatars: AvatarState[];
  motionFeedRef?: MutableRefObject<RoomMotionFeed>;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function bubbleLifetimeMs(text: string): number {
  return clamp(
    CHAT_BUBBLE_BASE_MS + text.length * CHAT_BUBBLE_PER_CHAR_MS,
    CHAT_BUBBLE_MIN_MS,
    CHAT_BUBBLE_MAX_MS
  );
}

function disposeBubble(entry: AnchoredChatBubble): void {
  entry.bubble.texture.dispose();
}

function overlapsHorizontally(a: ScreenRect, b: ScreenRect): boolean {
  return a.left < b.right && a.right > b.left;
}

export function RoomChatBubbles({ roomId, avatars, motionFeedRef }: Props) {
  const { camera, size } = useThree();
  const [bubbles, setBubbles] = useState<AnchoredChatBubble[]>([]);
  const bubblesRef = useRef<AnchoredChatBubble[]>([]);
  const seenByAvatarRef = useRef<Map<string, string>>(new Map());
  const bubbleGroupRefs = useRef<Map<string, THREE.Group>>(new Map());
  const bubbleMatRefs = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map());
  const lastPruneAtRef = useRef(0);

  const projectionRef = useRef({
    point: new THREE.Vector3(),
    origin: new THREE.Vector3(0, 0, 0),
    offset: new THREE.Vector3(),
    center: new THREE.Vector3(),
    centerShifted: new THREE.Vector3(),
    cameraUp: new THREE.Vector3(),
    cameraRight: new THREE.Vector3(),
  });

  const renderBubbles = useMemo(
    () => [...bubbles].sort((left, right) => left.at - right.at),
    [bubbles]
  );

  useEffect(() => {
    bubblesRef.current = bubbles;
  }, [bubbles]);

  useEffect(() => {
    return () => {
      for (const entry of bubblesRef.current) {
        disposeBubble(entry);
      }
    };
  }, []);

  useEffect(() => {
    seenByAvatarRef.current.clear();
    setBubbles((previous) => {
      for (const entry of previous) {
        disposeBubble(entry);
      }
      return [];
    });
  }, [roomId]);

  useEffect(() => {
    if (avatars.length === 0) {
      return;
    }
    const now = Date.now();
    const additions: AnchoredChatBubble[] = [];

    for (const avatar of avatars) {
      const text = avatar.lastMessage?.trim() ?? "";
      const atCandidate = avatar.lastMessageAt;
      if (
        !text ||
        typeof atCandidate !== "number" ||
        !Number.isFinite(atCandidate)
      ) {
        continue;
      }
      const at = atCandidate;
      const messageKey = `${at}:${text}`;
      if (seenByAvatarRef.current.get(avatar.id) === messageKey) {
        continue;
      }
      seenByAvatarRef.current.set(avatar.id, messageKey);

      const lifetimeMs = bubbleLifetimeMs(text);
      const ageMs = now - at;
      if (ageMs > lifetimeMs + CHAT_BUBBLE_FADE_MS + CHAT_BUBBLE_STALE_GRACE_MS) {
        continue;
      }
      const livePose = motionFeedRef?.current.byId.get(avatar.id);
      const anchorX = livePose?.x ?? avatar.x;
      const anchorY = (livePose?.y ?? avatar.y) + CHAT_BUBBLE_WORLD_BASE_Y;
      const anchorZ = livePose?.z ?? avatar.z;

      additions.push({
        id: `${avatar.id}:${at}`,
        avatarId: avatar.id,
        at,
        lifetimeMs,
        anchorX,
        anchorY,
        anchorZ,
        // Habbo-style readability: no pointer tail, clean floating card look.
        bubble: createBubbleTexture(avatar.name, text, false),
      });
    }

    if (additions.length === 0) {
      return;
    }

    setBubbles((previous) => {
      const nowMs = Date.now();
      const kept: AnchoredChatBubble[] = [];
      for (const entry of previous) {
        const expired = nowMs - entry.at > entry.lifetimeMs + CHAT_BUBBLE_FADE_MS;
        if (expired) {
          disposeBubble(entry);
        } else {
          kept.push(entry);
        }
      }

      const combined = [...kept, ...additions];
      if (combined.length <= MAX_ACTIVE_BUBBLES) {
        return combined;
      }

      const overflow = combined.length - MAX_ACTIVE_BUBBLES;
      for (let index = 0; index < overflow; index += 1) {
        disposeBubble(combined[index]);
      }
      return combined.slice(overflow);
    });
  }, [avatars, motionFeedRef]);

  useFrame(() => {
    const active = bubblesRef.current;
    if (active.length === 0) {
      return;
    }

    const now = Date.now();
    const tools = projectionRef.current;
    const placedRects: ScreenRect[] = [];
    const newestFirst = [...active].sort((left, right) => right.at - left.at);
    const currentZoom = Number.isFinite(
      (camera as THREE.OrthographicCamera).zoom
    )
      ? (camera as THREE.OrthographicCamera).zoom
      : 1;
    const zoomScale = clamp(
      CHAT_BUBBLE_REFERENCE_ZOOM / Math.max(0.001, currentZoom),
      CHAT_BUBBLE_MIN_ZOOM_SCALE,
      CHAT_BUBBLE_MAX_ZOOM_SCALE
    );
    const effectiveScale = CHAT_BUBBLE_BASE_SCALE * zoomScale;

    tools.cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion).normalize();
    tools.cameraRight
      .set(1, 0, 0)
      .applyQuaternion(camera.quaternion)
      .normalize();

    const projectPoint = (source: THREE.Vector3): { x: number; y: number } => {
      tools.point.copy(source).project(camera);
      return {
        x: (tools.point.x * 0.5 + 0.5) * size.width,
        y: (-tools.point.y * 0.5 + 0.5) * size.height,
      };
    };

    tools.offset.copy(tools.origin).add(tools.cameraUp);
    const originScreen = projectPoint(tools.origin);
    const upScreen = projectPoint(tools.offset);
    tools.offset.copy(tools.origin).add(tools.cameraRight);
    const rightScreen = projectPoint(tools.offset);
    const pixelsPerUpUnit = Math.max(
      0.001,
      Math.hypot(upScreen.x - originScreen.x, upScreen.y - originScreen.y)
    );
    const pixelsPerRightUnit = Math.max(
      0.001,
      Math.hypot(rightScreen.x - originScreen.x, rightScreen.y - originScreen.y)
    );

    for (const entry of newestFirst) {
      const material = bubbleMatRefs.current.get(entry.id);
      const group = bubbleGroupRefs.current.get(entry.id);
      if (!material || !group) {
        continue;
      }

      const ageMs = now - entry.at;
      if (ageMs > entry.lifetimeMs + CHAT_BUBBLE_FADE_MS) {
        material.opacity = 0;
        continue;
      }
      if (ageMs <= entry.lifetimeMs) {
        material.opacity = 1;
      } else {
        const fadeProgress = (ageMs - entry.lifetimeMs) / CHAT_BUBBLE_FADE_MS;
        material.opacity = 1 - clamp(fadeProgress, 0, 1);
      }

      const visibleWidth = entry.bubble.contentPlaneW;
      const visibleHeight = entry.bubble.contentPlaneH;

      const baseCenter = tools.center
        .set(entry.anchorX, entry.anchorY, entry.anchorZ)
        .addScaledVector(tools.cameraUp, (visibleHeight * effectiveScale) / 2);

      const bubbleWidthPx =
        visibleWidth * effectiveScale * pixelsPerRightUnit;
      const bubbleHeightPx =
        visibleHeight * effectiveScale * pixelsPerUpUnit;
      let verticalShiftPx = 0;
      let rect: ScreenRect = {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      };

      for (let attempt = 0; attempt < 24; attempt += 1) {
        const shiftWorld = verticalShiftPx / pixelsPerUpUnit;
        const center = tools.centerShifted
          .copy(baseCenter)
          .addScaledVector(tools.cameraUp, shiftWorld);
        const centerScreen = projectPoint(center);
        rect = {
          left: centerScreen.x - bubbleWidthPx / 2,
          right: centerScreen.x + bubbleWidthPx / 2,
          top: centerScreen.y - bubbleHeightPx / 2,
          bottom: centerScreen.y + bubbleHeightPx / 2,
        };

        let neededShiftPx = 0;
        for (const placed of placedRects) {
          if (!overlapsHorizontally(rect, placed)) {
            continue;
          }
          const overlapsVertically =
            rect.top < placed.bottom + CHAT_BUBBLE_SCREEN_GAP_PX &&
            rect.bottom > placed.top - CHAT_BUBBLE_SCREEN_GAP_PX;
          if (!overlapsVertically) {
            continue;
          }
          const overlapPush =
            rect.bottom - placed.top + CHAT_BUBBLE_SCREEN_GAP_PX;
          if (overlapPush > neededShiftPx) {
            neededShiftPx = overlapPush;
          }
        }

        if (neededShiftPx <= 0.01) {
          break;
        }
        verticalShiftPx += neededShiftPx;
      }

      const finalShiftWorld = verticalShiftPx / pixelsPerUpUnit;
      group.scale.setScalar(effectiveScale);
      group.position.copy(baseCenter).addScaledVector(tools.cameraUp, finalShiftWorld);
      placedRects.push(rect);
    }

    if (now - lastPruneAtRef.current < 450) {
      return;
    }
    lastPruneAtRef.current = now;
    const hasExpired = active.some(
      (entry) => now - entry.at > entry.lifetimeMs + CHAT_BUBBLE_FADE_MS
    );
    if (!hasExpired) {
      return;
    }
    setBubbles((previous) => {
      const kept: AnchoredChatBubble[] = [];
      for (const entry of previous) {
        const expired = now - entry.at > entry.lifetimeMs + CHAT_BUBBLE_FADE_MS;
        if (expired) {
          disposeBubble(entry);
        } else {
          kept.push(entry);
        }
      }
      return kept;
    });
  });

  return (
    <>
      {renderBubbles.map((entry, index) => (
        <Billboard
          key={entry.id}
          ref={(group) => {
            if (group) {
              bubbleGroupRefs.current.set(entry.id, group);
            } else {
              bubbleGroupRefs.current.delete(entry.id);
            }
          }}
          position={[entry.anchorX, entry.anchorY, entry.anchorZ]}
        >
          <mesh renderOrder={200 + index}>
            <planeGeometry args={[entry.bubble.planeW, entry.bubble.planeH]} />
            <meshBasicMaterial
              ref={(material) => {
                if (material) {
                  bubbleMatRefs.current.set(entry.id, material);
                } else {
                  bubbleMatRefs.current.delete(entry.id);
                }
              }}
              map={entry.bubble.texture}
              transparent
              depthWrite={false}
              depthTest={false}
              toneMapped={false}
            />
          </mesh>
        </Billboard>
      ))}
    </>
  );
}
